import { useCallback, useEffect, useState } from "react"
import type { VotoInviato } from "../api/tipi"
import { type EsitoSvuotamento, accoda, quantiInCoda, svuota } from "./coda"

/** Ogni quanto ritentare mentre ci sono voti in sospeso. Il browser avvisa
 *  con l'evento `online`, ma su mobile quell'evento è ottimista: dice che
 *  c'è un'interfaccia di rete, non che il server risponda. */
const RITENTA_MS = 15_000

export function useCoda(token: string, proprietario: string) {
  const [inCoda, setInCoda] = useState(0)
  const [rifiutati, setRifiutati] = useState<EsitoSvuotamento["rifiutati"]>([])

  const aggiornaConteggio = useCallback(async () => {
    setInCoda(await quantiInCoda(proprietario))
  }, [proprietario])

  const drena = useCallback(async () => {
    try {
      const esito = await svuota(token, proprietario)
      if (esito.rifiutati.length > 0) setRifiutati(esito.rifiutati)
    } catch {
      /* rete ancora assente: la coda resta intatta e si ritenta dopo */
    }
    await aggiornaConteggio()
  }, [token, proprietario, aggiornaConteggio])

  useEffect(() => {
    void drena()
  }, [drena])

  useEffect(() => {
    const alRitorno = () => void drena()
    addEventListener("online", alRitorno)
    return () => removeEventListener("online", alRitorno)
  }, [drena])

  useEffect(() => {
    if (inCoda === 0) return
    const t = setInterval(() => void drena(), RITENTA_MS)
    return () => clearInterval(t)
  }, [inCoda, drena])

  const metti = useCallback(
    async (voto: VotoInviato) => {
      await accoda(voto, proprietario)
      await aggiornaConteggio()
    },
    [proprietario, aggiornaConteggio],
  )

  return {
    inCoda,
    rifiutati,
    metti,
    drena,
    dimenticaRifiutati: useCallback(() => setRifiutati([]), []),
  }
}
